// Phone notifications (roadmap #61, #62) — the pure scheduler. The app knows
// what's coming (dated releases in the library, the next episode of airing
// series, subscribed comics), so it builds the next two weeks of notices
// itself and hands them to the Worker, which pushes each one when it's due.

export const PUSH_CATEGORIES = [
	{ value: "videogame", label: "Games" },
	{ value: "movie", label: "Movies" },
	{ value: "book", label: "Books" },
	{ value: "tv", label: "TV episodes" },
	{ value: "anime", label: "Anime episodes" },
	{ value: "comic", label: "Comics" },
];

export const DEFAULT_PUSH_SETTINGS = {
	categories: Object.fromEntries(PUSH_CATEGORIES.map((c) => [c.value, true])),
	hour: 9, // when on the day to notify (local time)
	quiet: { from: 22, to: 8 }, // never notify in these hours (local)
	digest: false, // one notification a day instead of one per release
};

export function normalizePushSettings(s) {
	const d = DEFAULT_PUSH_SETTINGS;
	const hour = (v, fb) => (Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 23 ? Number(v) : fb);
	return {
		categories: { ...d.categories, ...(s?.categories ?? {}) },
		hour: hour(s?.hour, d.hour),
		quiet: s?.quiet === null ? null : { from: hour(s?.quiet?.from, d.quiet.from), to: hour(s?.quiet?.to, d.quiet.to) },
		digest: !!s?.digest,
	};
}

// Is `h` inside quiet hours (which may wrap midnight)?
export function inQuietHours(h, quiet) {
	if (!quiet || quiet.from === quiet.to) return false;
	return quiet.from < quiet.to ? h >= quiet.from && h < quiet.to : h >= quiet.from || h < quiet.to;
}

export function notifyHour(settings) {
	const s = normalizePushSettings(settings);
	return inQuietHours(s.hour, s.quiet) ? s.quiet.to : s.hour;
}

const addDays = (date, n) => {
	const d = new Date(`${date}T12:00:00Z`);
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
};

// Local wall-clock (date, hour) → an ISO instant. Injected in tests.
export const localInstant = (date, hour) => new Date(`${date}T${String(hour).padStart(2, "0")}:00:00`).toISOString();

const SKIP_STATUSES = new Set(["not_interested", "bounced"]);

// → [{ id, at, title, body, url }], soonest first.
export function buildPushNotices({ items = [], episodes = [], comics = [], settings, today, days = 14, toInstant = localInstant }) {
	const s = normalizePushSettings(settings);
	const end = addDays(today, days);
	const hour = notifyHour(s);
	const rows = [];
	const inWindow = (d) => d && d >= today && d <= end;

	for (const i of items) {
		if (!s.categories[i.media_type] || SKIP_STATUSES.has(i.engagement_status)) continue;
		if (i.release_precision && i.release_precision !== "day") continue;
		if (!/^\d{4}-\d{2}-\d{2}$/.test(i.release_date ?? "") || !inWindow(i.release_date)) continue;
		rows.push({ id: `rel:${i.id}:${i.release_date}`, date: i.release_date, title: `${i.title} is out today`, body: i.mediaLabel ?? "", url: i.route ?? "/" });
	}
	for (const e of episodes) {
		if (!s.categories[e.media_type] || !inWindow(e.release_date)) continue;
		rows.push({ id: `ep:${e.id}`, date: e.release_date, title: `New episode: ${e.title}`, body: e.secondary ?? "", url: e.route ?? "/" });
	}
	for (const c of comics) {
		if (!s.categories.comic || !inWindow(c.release_date)) continue;
		rows.push({ id: `comic:${c.id}`, date: c.release_date, title: `${c.title} is on sale`, body: c.secondary ?? "", url: c.route ?? "/" });
	}

	const seen = new Set();
	const unique = rows.filter((r) => (seen.has(r.id) ? false : seen.add(r.id))).sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
	if (!s.digest) return unique.map(({ date, ...n }) => ({ ...n, at: toInstant(date, hour) }));

	// Daily digest: one notice per day.
	const byDay = new Map();
	for (const r of unique) {
		if (!byDay.has(r.date)) byDay.set(r.date, []);
		byDay.get(r.date).push(r);
	}
	return [...byDay.entries()].map(([date, list]) => ({
		id: `digest:${date}:${list.length}`,
		at: toInstant(date, hour),
		title: list.length === 1 ? list[0].title : `${list.length} things out today`,
		body: list.length === 1 ? list[0].body : list.map((r) => r.title.replace(/ is (out today|on sale)$/, "")).join(" · ").slice(0, 200),
		url: list.length === 1 ? list[0].url : "/upcoming",
	}));
}
